import React from "react";
import { useTranslation } from "react-i18next";
import { TLanguage } from "../../Common/other";
import { LANGUAGES } from "../../constants/other";
import Footer from "../Footer";
import Menu from "../Menu";
import {
  COMPANY_EMAIL,
  COMPANY_PHONE,
  PRIVACY_POLICY_CONTENT,
  TPolicyBlock,
} from "./PrivacyPolicy.content";
import styles from "./PrivacyPolicy.module.scss";

// Matches URLs and email addresses inside a sentence so they can be rendered as links.
const LINK_REGEX =
  /(https?:\/\/[^\s]*[^\s.,;)]|[\w.+-]+@[\w-]+(?:\.[\w-]+)+)/g;

const renderText = (text: string) =>
  text.split(LINK_REGEX).map((part, index) => {
    if (index % 2 === 0) {
      return <React.Fragment key={index}>{part}</React.Fragment>;
    }
    const isUrl = part.startsWith("http");
    return (
      <a
        key={index}
        href={isUrl ? part : `mailto:${part}`}
        target={isUrl ? "_blank" : undefined}
        rel={isUrl ? "noopener noreferrer" : undefined}
      >
        {part}
      </a>
    );
  });

const renderBlock = (block: TPolicyBlock, index: number) => {
  switch (block.type) {
    case "h4":
      return <h4 key={index}>{block.text}</h4>;
    case "ul":
      return (
        <ul key={index}>
          {block.items.map((item, itemIndex) => (
            <li key={itemIndex}>{renderText(item)}</li>
          ))}
        </ul>
      );
    default:
      return <p key={index}>{renderText(block.text)}</p>;
  }
};

const PrivacyPolicyPresenter = () => {
  const { t, i18n } = useTranslation(["translation"]);
  const content =
    PRIVACY_POLICY_CONTENT[i18n.language as TLanguage] ??
    PRIVACY_POLICY_CONTENT[LANGUAGES.vn];

  return (
    <>
      <main>
        <Menu />
        <div className={styles.privacy}>
          <section className={styles.privacy__head}>
            <div className={styles.privacy__head__container}>
              <h1 className="text-blue-2">{t("PRIVACY_POLICY")}</h1>
              <p>
                {t("LAST_UPDATED")}: {content.updatedAt}
              </p>
            </div>
          </section>

          <section className={styles.privacy__content}>
            <div className={styles.privacy__content__container}>
              <p className={styles.privacy__content__container__intro}>
                {content.intro}
              </p>

              {content.sections.map((section) => (
                <div
                  key={section.id}
                  id={section.id}
                  className={styles.privacy__content__container__section}
                >
                  <h3>{section.title}</h3>
                  {section.blocks.map(renderBlock)}
                </div>
              ))}

              <div className={styles.privacy__content__container__company}>
                <h4>{content.company.brand}</h4>
                <p>{content.company.legalName}</p>
                <p>
                  {t("ADDRESS")}: {t("LOCA")}
                </p>
                <p>
                  {t("PHONE_NUM")}:{" "}
                  <a href={`tel:${COMPANY_PHONE.replace(/\s/g, "")}`}>
                    {COMPANY_PHONE}
                  </a>
                </p>
                <p>
                  {`Email`}:{" "}
                  <a href={`mailto:${COMPANY_EMAIL}`}>{COMPANY_EMAIL}</a>
                </p>
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </main>
    </>
  );
};

export default PrivacyPolicyPresenter;
